import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'next-i18next';
import { useSetAtom } from 'jotai';
import { BuildingOfficeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

import { Heading } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

import { EmployerCompanyModalFooterContext } from './employer-company-modal-footer-context';
import { currentCompanyActionAtom, isCompanyForeignAtom, selectedCompanyAtom } from '../../employer-companies.atoms';

/** Conventional FIC values for foreign companies */
const FOREIGN_COMPANY_DEFAULTS = {
  province_code: 'EE',
  zip_code: '00000',
  sdi_code: 'XXXXXXX',
  fiscal_code: '',
};

export type CompanyTypeChoiceRef = {
  submit: () => void;
};

interface CompanyTypeChoiceProps {
  hideCta?: boolean;
  onChooseItalian?: () => void;
  onChooseForeign?: () => void;
}

const CompanyTypeChoice = forwardRef<CompanyTypeChoiceRef, CompanyTypeChoiceProps>(
  ({ hideCta, onChooseItalian, onChooseForeign }, ref) => {
    const { t } = useTranslation('payment-and-invoices');
    const footerContext = useContext(EmployerCompanyModalFooterContext);
    const setCurrentCompanyAction = useSetAtom(currentCompanyActionAtom);
    const setIsCompanyForeign = useSetAtom(isCompanyForeignAtom);
    const setSelectedCompany = useSetAtom(selectedCompanyAtom);

    const handleChooseItalian = useCallback(() => {
      setIsCompanyForeign(false);
      if (onChooseItalian) {
        onChooseItalian();
      } else {
        setCurrentCompanyAction('searchCompany');
      }
    }, [onChooseItalian, setCurrentCompanyAction, setIsCompanyForeign]);

    const handleChooseForeign = useCallback(() => {
      setIsCompanyForeign(true);
      setSelectedCompany(FOREIGN_COMPANY_DEFAULTS);
      if (onChooseForeign) {
        onChooseForeign();
      } else {
        setCurrentCompanyAction('createCompany');
      }
    }, [onChooseForeign, setCurrentCompanyAction, setIsCompanyForeign, setSelectedCompany]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        // No-op: choice is made by clicking a card directly
      },
    }));

    // Hide footer in modal context (no next/cancel needed, cards navigate directly)
    useEffect(() => {
      if (footerContext) {
        footerContext.setFooter(null);
        return () => footerContext.setFooter(null);
      }
    }, [footerContext]);

    return (
      <Wrapper>
        <Heading size={3} weight="semibold" color="neutral" className="text-center w-full mb-2">
          {t('choose_company_type')}
        </Heading>
        <Subtitle>{t('choose_company_type_subtitle')}</Subtitle>
        <CardsWrapper>
          <ChoiceCard onClick={handleChooseItalian}>
            <IconWrapper>
              <BuildingOfficeIcon className="h-8 w-8 text-primary-500" />
            </IconWrapper>
            <CardTitle>{t('italian_company')}</CardTitle>
            <CardDescription>{t('italian_company_description')}</CardDescription>
          </ChoiceCard>
          <ChoiceCard onClick={handleChooseForeign}>
            <IconWrapper>
              <GlobeAltIcon className="h-8 w-8 text-primary-500" />
            </IconWrapper>
            <CardTitle>{t('foreign_company')}</CardTitle>
            <CardDescription>{t('foreign_company_description')}</CardDescription>
          </ChoiceCard>
        </CardsWrapper>
      </Wrapper>
    );
  }
);

CompanyTypeChoice.displayName = 'CompanyTypeChoice';
export default CompanyTypeChoice;

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col items-center justify-center h-full px-4 md:px-0', className)} {...props} />
);

const CardsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid grid-cols-1 gap-6 w-full max-w-[500px] md:grid-cols-2', className)} {...props} />
);

const ChoiceCard = ({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    className={cn(
      'flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-neutral-40 bg-neutral-10 cursor-pointer transition-all hover:border-primary-500 hover:shadow-md focus:outline-none focus:border-primary-500 focus:shadow-md',
      className
    )}
    {...props}
  />
);

const IconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center justify-center w-14 h-14 rounded-full bg-primary-50', className)} {...props} />
);

const CardTitle = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-base font-semibold text-neutral-700', className)} {...props} />
);

const CardDescription = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-sm text-neutral-500 text-center', className)} {...props} />
);

const Subtitle = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn('text-base font-normal text-neutral-500 text-center w-full mt-0 mb-10 lg:mb-16', className)}
    {...props}
  />
);
